export const TENANT_ID = 'telus-abhealth';
export const APP_ID = 'telus-abhealth-web';
export const AUTH0_CONNECTION = 'telus-abhealth';

export const SENTRY_CONSTANTS = {
  OWNERSHIP_TEAM: 'telus-abhealth-solutions',
};

export const AUTH0_ERROR_TYPES = {
  LOGIN_REQUIRED: 'login_required',
};

/**
 * Live chat analytics taxonomy (ticket).
 *
 * - `liveChatButton.*.eventId` — Segment / Airtable **button** event for floating CTA
 *   (`reckrHgobYFGTtoJz` homepage, `recPfMffTkW4xrEWE` authenticated care services).
 * - `liveChatButton.*.content.id` — BE **content** record id (different from button `eventId`).
 * - `pageName.*.eventId` — screen / page-view style ids for home vs care surfaces (wire on
 *   `SCREEN_LOADED` / page views when needed).
 */
export const LIVE_CHAT_ANALYTICS = {
  liveChatButton: {
    homepage: {
      eventId: 'reckrHgobYFGTtoJz',
      screenName: 'home screen',
      detail: 'start a patient navigator live chat',
      content: {
        id: '7CuY2Sr40ZHmB1b0diT9Si',
        name: 'Patient Navigating Live Chat Section Unauthenticated',
        system: 'masonry',
      },
      node: {
        id: 'patientNavigatorLiveChatUnauthenticated',
        label: 'patient navigator live chat section unauthenticated',
        type: 'verticalLayout',
        parent_label: 'homepage',
        parent_type: 'verticalLayout',
      },
    },
    authenticatedCareServices: {
      eventId: 'recPfMffTkW4xrEWE',
      screenName: 'authenticated services screen',
      detail: 'start a patient navigator live chat',
      content: {
        id: '7CuY2Sr40ZHmB1b0diT9Si',
        name: 'Patient Navigating Live Chat Section Authenticated',
        system: 'web-live chat SDK',
      },
      node: {
        id: 'patientNavigatorLiveChatAuthenticated',
        label: 'patient navigator live chat section authenticated',
        type: 'verticalLayout',
        parent_label: 'get help navigating services',
        parent_type: 'web-live chat sdk',
      },
    },
  },
  pageName: {
    homeScreen: {
      eventId: 'recHl6vZHSc84JgtF',
    },
    authenticatedCareServices: {
      eventId: 'recQkNGS0ZQ6Ch4Nl',
    },
    unauthenticatedCareServices: {
      eventId: 'recxEEuWlJB7Tf5If',
    },
  },
} as const;

export type LiveChatFloatingButtonAnalyticsContext =
  keyof typeof LIVE_CHAT_ANALYTICS.liveChatButton;

export type LiveChatFloatingButtonAnalyticsPayload =
  (typeof LIVE_CHAT_ANALYTICS.liveChatButton)[LiveChatFloatingButtonAnalyticsContext];

export type LiveChatPageNameAnalyticsContext =
  keyof typeof LIVE_CHAT_ANALYTICS.pageName;

export const getLiveChatFloatingButtonAnalytics = (
  context: LiveChatFloatingButtonAnalyticsContext,
): LiveChatFloatingButtonAnalyticsPayload =>
  LIVE_CHAT_ANALYTICS.liveChatButton[context];

export const getLiveChatPageNameEventId = (
  context: LiveChatPageNameAnalyticsContext,
): (typeof LIVE_CHAT_ANALYTICS.pageName)[LiveChatPageNameAnalyticsContext]['eventId'] =>
  LIVE_CHAT_ANALYTICS.pageName[context].eventId;

export const MASONRY_NAMESPACES = {
  MASONRY_TELUS_NAMESPACE: 'telus_abhealth',
  MASONRY_TELUS_ABHEALTH_NAMESPACE: 'telus-abhealth',
};

// Cross-platform contract; matches the iOS/Services namespace and node/action types exactly (TLAB-3207).
export const MOBILE_HEALTH_CARD = {
  NAMESPACE: 'telus-abhealth',
  ACTION_TYPE: 'mobileHealthCardLinkout',
  ERROR_NODE_TYPE: 'mobileHealthCardError',
  DATA_PROCESSOR_ID: 'mobileHealthCardLinkout',
  OUTCOME_FIELD_ID: 'outcome',
  DESTINATION_URL_FIELD_ID: 'destinationUrl',
  MESSAGE_FIELD_ID: 'message',
  NAVIGATE_OUTCOME: 'navigate',
} as const;
export const MASONRY_WIDGET_TYPES = {
  PRIVACY_POLICY: 'consentManagement',
} as const;

export const MASONRY_ENDPOINTS = {
  MASONRY_CLIENT_VERSION: APP_ID,
  MASONRY_CLIENT_PLATFORM: 'web',
  MASONRY_CLIENT_SIZE: 'laptop',
};

/** Literal so Masonry `overrideRenderer`/`registerRenderer` infer `RendererNodeForType<..., 'ctaCard'>` (not `string`, which widens to the whole driver node union). */
const MASONRY_NODE_TYPE_CTA_CARD = 'ctaCard' as const;
const MASONRY_NODE_TYPE_RICH_TEXT = 'richtext' as const;
const MASONRY_NODE_TYPE_PROGRESS_BAR = 'progressBar' as const;
const MASONRY_NODE_TYPE_TEXT_ACTION = 'textAction' as const;

export const MASONRY = {
  ROOT_NODE_ID_HOME_PAGE: 'homepage$root',
  ROOT_NODE_ID_CARE_SERVICES: 'care-services$root',
  ROOT_NODE_ID_HEALTH_TOPICS: 'healthTopics$root',
  ROOT_NODE_ID_PRIVACY_POLICY_IN_APP:
    'consentManagement$unauthenticatedConsentManagement',
  ROOT_NODE_ID_PRIVACY_POLICY:
    'consentManagement$authenticatedConsentManagement',
  ROOT_NODE_ID_FIND_SUPPORT: 'findSupport$root',
  ROOT_NODE_ID_SYSTEM_BANNERS_LOGIN:
    'login$systemNotificationBannerLoginSection',
  API_VERSION: 2,
  NODE_TYPE_CTA_CARD: MASONRY_NODE_TYPE_CTA_CARD,
  NODE_TYPE_RICH_TEXT: MASONRY_NODE_TYPE_RICH_TEXT,
  NODE_TYPE_PROGRESS_BAR: MASONRY_NODE_TYPE_PROGRESS_BAR,
  NODE_TYPE_TEXT_ACTION: MASONRY_NODE_TYPE_TEXT_ACTION,
};

export const SECONDARY_CONTENT_MAX_WIDTH = '772px';

export enum FEATURE_FLAGS {
  TELUS_ALBERTA_LIVE_CHAT_ENABLED = 'config_telus_alberta_live_chat_enabled',
  MY_HEALTH_REDESIGN_ENABLED = 'config_app_solution_services_my_health_redesign_enabled',
}

// Cross-platform contract; iOS, Android and Web read and write this exact field (TLAB-3087).
export const MY_HEALTH_REDESIGN_ANNOUNCEMENT_MODAL_SEEN_FIELD =
  'services_my_health_redesign_announcement_modal_seen';

export enum QUERY_PARAM_KEYS {
  MODAL = 'modal',
  FLOW = 'flow',
}

export enum QUERY_PARAM_MODAL_VALUE {
  REMOVE_PERSONALIZATION = 'remove-personalization',
  PERSONALIZATION_REMOVED_DISCLAIMER = 'personalization-removed-disclaimer',
  CHAT_SUPPORT = 'chat-support',
}

export const YEAR_DATE_FORMAT_UTC: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  timeZone: 'UTC',
};
